import { PACKAGE_KEYS } from '@/api/services/billingProducts';

import { parseBuyLocation, productFromPath } from './purchase';

/**
 * A purchase started before the user could buy: signed out, or with an
 * account still waiting for e-mail verification or approval. It is the
 * `/buy/...` path the user opened, nothing more; the login page sends the
 * user back to it.
 *
 * localStorage, not sessionStorage: the verification link opens in a new tab
 * and approval can take days, and a tab's sessionStorage survives neither.
 * The path is not a secret, but it expires, and logging out forgets it.
 */
export const PURCHASE_INTENT_KEY = 'pendingPurchase';

/** The Stripe round trip returns to fixed URLs; this tab remembers which
 *  purchase it left from, so the cancel page can link back to it. */
export const LAST_CHECKOUT_KEY = 'lastCheckoutPath';

export const PURCHASE_INTENT_TTL_DAYS = 7;
export const PURCHASE_INTENT_TTL_MS = PURCHASE_INTENT_TTL_DAYS * 24 * 60 * 60 * 1000;

const SAFE_BASE = 'https://app.invalid';

/** The query parameter each product reads; any other is dropped. */
const PARAM_FOR: Record<string, string | undefined> = {
  seats: 'quantity',
  hours: 'quantity',
  topup: 'amount',
};

/**
 * `value` as a same-origin `/buy/...` path of a known product, rebuilt from
 * its parts, or null. Refuses other origins, protocol-relative `//host`,
 * backslashes, whitespace, control characters, `..` escapes, unknown
 * products and malformed quantities; drops a trailing slash and keeps only
 * the product's own query parameter.
 */
export function safeBuyPath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith('/buy/') || value.startsWith('//')) return null;
  if (/[\\\s]/.test(value) || hasControlCharacter(value)) return null;
  let url: URL;
  try {
    url = new URL(value, SAFE_BASE);
  } catch {
    return null;
  }
  if (url.origin !== SAFE_BASE) return null;
  const product = productFromPath(url.pathname);
  if (!product) return null;
  const pathname = (PACKAGE_KEYS as readonly string[]).includes(product)
    ? `/buy/package/${product}`
    : `/buy/${product}`;

  const param = PARAM_FOR[product];
  const kept = new URLSearchParams();
  const raw = param ? url.searchParams.get(param) : null;
  if (param && raw !== null) kept.set(param, raw);
  const search = kept.toString() ? `?${kept.toString()}` : '';
  if (!parseBuyLocation(pathname, search).ok) return null;
  return `${pathname}${search}`;
}

function hasControlCharacter(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

type StoredIntent = { path: string; savedAt: number };

function read(now: number): string | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(PURCHASE_INTENT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredIntent>;
    if (typeof parsed.path !== 'string' || typeof parsed.savedAt !== 'number') return null;
    if (now - parsed.savedAt > PURCHASE_INTENT_TTL_MS || parsed.savedAt > now) return null;
    return safeBuyPath(parsed.path);
  } catch {
    return null;
  }
}

/** Keep `path` for after sign-in. Ignored unless it is a safe `/buy/` path. */
export function savePurchaseIntent(path: string, now = Date.now()) {
  const safe = safeBuyPath(path);
  if (!safe) return;
  try {
    const value: StoredIntent = { path: safe, savedAt: now };
    window.localStorage.setItem(PURCHASE_INTENT_KEY, JSON.stringify(value));
  } catch {
    /* storage blocked: the purchase is simply not remembered */
  }
}

/** The kept purchase, if one is waiting and still fresh. */
export function peekPurchaseIntent(now = Date.now()): string | null {
  return read(now);
}

/** Read and forget the kept purchase. */
export function takePurchaseIntent(now = Date.now()): string | null {
  const path = read(now);
  clearPurchaseIntent();
  return path;
}

export function clearPurchaseIntent() {
  try {
    window.localStorage.removeItem(PURCHASE_INTENT_KEY);
  } catch {
    /* storage blocked */
  }
}

export function rememberCheckoutPath(path: string) {
  const safe = safeBuyPath(path);
  if (!safe) return;
  try {
    window.sessionStorage.setItem(LAST_CHECKOUT_KEY, safe);
  } catch {
    /* storage blocked */
  }
}

export function lastCheckoutPath(): string | null {
  try {
    return safeBuyPath(window.sessionStorage.getItem(LAST_CHECKOUT_KEY));
  } catch {
    return null;
  }
}
